'use client'

import { useEffect, useRef, useSyncExternalStore, type MouseEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { cx } from './utils/cx'
import { StripeTag, type StripeTagVariant } from './StripeTag'

interface DialogProps {
  open: boolean
  onClose?: () => void
  size?: 'default' | 'lg' | 'full'
  stripe?: { label: ReactNode; variant?: StripeTagVariant }
  title?: ReactNode
  desc?: ReactNode
  actions?: ReactNode
  children?: ReactNode
  closeOnBackdrop?: boolean
  showClose?: boolean
  blur?: boolean
  className?: string
  ariaLabel?: string
  ariaLabelledBy?: string
}

const subscribe = () => () => {}
const getSnapshot = () => true
const getServerSnapshot = () => false

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export function Dialog({
  open,
  onClose,
  size,
  stripe,
  title,
  desc,
  actions,
  children,
  closeOnBackdrop = true,
  showClose = true,
  blur,
  className,
  ariaLabel,
  ariaLabelledBy
}: DialogProps) {
  const mounted = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
  const dialogRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const previouslyFocused = document.activeElement as HTMLElement | null

    document.body.classList.add('st-modal-open')
    if (blur) document.body.classList.add('st-modal-blur')

    const dialog = dialogRef.current
    const focusable = dialog?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
    focusable?.[0]?.focus()

    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose?.()
        return
      }
      if (e.key !== 'Tab' || !dialog) return
      const items = dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      if (items.length === 0) return
      const first = items[0]
      const last = items[items.length - 1]
      const active = document.activeElement
      if (e.shiftKey && active === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && active === last) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', handler)

    return () => {
      document.body.classList.remove('st-modal-open')
      document.body.classList.remove('st-modal-blur')
      document.removeEventListener('keydown', handler)
      previouslyFocused?.focus?.()
    }
  }, [open, onClose, blur])

  const onBackdrop = (e: MouseEvent<HTMLDivElement>) => {
    if (closeOnBackdrop && e.target === e.currentTarget) onClose?.()
  }

  const blurStyle = blur
    ? ({
        backdropFilter: 'blur(14px) saturate(0.7)',
        WebkitBackdropFilter: 'blur(14px) saturate(0.7)'
      } as React.CSSProperties)
    : undefined

  const node = (
    <div
      className={cx('st-backdrop', open && 'on', blur && 'st-blur')}
      style={blurStyle}
      onClick={onBackdrop}
      role={open ? 'dialog' : undefined}
      aria-modal={open ? 'true' : undefined}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}>
      <div ref={dialogRef} className={cx('st-dialog', size, className)} onClick={(e) => e.stopPropagation()}>
        {stripe && <StripeTag variant={stripe.variant || 'accent'}>{stripe.label}</StripeTag>}
        {showClose && onClose && (
          <button className="st-dialog-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        )}
        {title && <h2>{title}</h2>}
        {desc && <p className="st-dialog-desc">{desc}</p>}
        {children}
        {actions && <div className="st-dialog-actions">{actions}</div>}
      </div>
    </div>
  )

  if (!mounted) return null
  return createPortal(node, document.body)
}
